import { html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import { ApiService } from '@services/api.service';
import { REFI_LIENHOLDER_CONST } from '../../shared/constants/app.constants';
import { FeatureToggle } from '@models/feature-toggle.model';
import { TagKey } from '@cof/tag-catalog-fs-lienholder-mfe';
import { stratum } from '../../../constants/stratum.js';
import { DebugService } from '@services/debug.service';
import { LenderService } from '@services/lender.service';
import { SessionService } from '@services/session.service';
import { environment } from '../../../environment/environment.js';
import { ExperimentService } from '@services/experiment.service';
import './lender-edit/lender-edit.js';
import './lender-review/lender-review.js';
import { Experiments } from '@models/experiment.model.js';
import { setIsV2Routing } from '@services/routing-v2.service.ts';

@customElement('refi-lender-checkout')
export class RefiLenderCheckout extends LitElement {
  declare apiErrorMessage: string;
  declare checkout: string;
  declare debug: DebugService;
  declare editBinded: any;
  declare isLoading: boolean;
  declare lenderInfo: any;
  declare loading: boolean;
  declare mockSession: any;
  declare showMissingFieldsMessage: boolean;
  declare testCheckout: boolean;
  declare featureToggles: FeatureToggle[];
  constructor() {
    super();
    this.debug = new DebugService('RefiLenderReview');
    this.apiErrorMessage = '';
    this.loading = false;
    this.lenderInfo = {};
    this.showMissingFieldsMessage = false;
    this.mockSession = {};
    this.editBinded = this.onEdit.bind(this);
    this.testCheckout = false;

    this.checkout = 'review';
    if (
      (window.location.hash &&
        window.location.hash.includes('#/nonprod/lienholder/')) ||
      this.testCheckout
    ) {
      if (!environment.isProdEnv) {
        this.mockSession = SessionService.mockSession;
      }
    }
  }

  static properties = {
    apiErrorMessage: { type: String },
    loading: { type: Boolean },
    lenderInfo: { type: Object },
    lenderInfoPrefilled: { type: Boolean },
    checkout: { type: String, attribute: 'checkout-page' },
    testCheckout: { type: Boolean, attribute: 'test-checkout' },
    experiments: {
      type: Object,
      hasChanged: (newVal: Experiments, oldVal: Experiments) => {
        if (newVal !== oldVal) {
          // loop through this.experiments and add the object name to experimentIds array if it's eligible
          const experimentIds: string[] = [];
          Object.keys(newVal).forEach((experiment) => {
            if (newVal[experiment].isEligible) {
              experimentIds.push(newVal[experiment].allocation);
            } else {
              experimentIds.push(`${experiment}-ineligible`);
            }
          });
          ExperimentService.updateExperiments(experimentIds);
          return true;
        }
        return false;
      },
    },
    v2Routing: {
      type: Boolean,
      attribute: 'v2-routing',
      reflect: true,
      hasChanged: (newVal: boolean) => {
        setIsV2Routing(newVal);
        return true;
      },
    },
    featureToggles: { type: Object, attribute: 'feature-toggles' },
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('lienholder-edit', this.editBinded);
    const bffPostappRouting = this.featureToggles?.find(
      (t) =>
        t.featureKey ===
        REFI_LIENHOLDER_CONST.FEATURE_TOGGLES.BFF_POSTAPP_ROUTING
    );
    if (bffPostappRouting?.isEnabled) {
      ApiService.setUpstreamHeader('gen3');
    }
    // route for functional testing
    if (
      (!environment.isProdEnv &&
        window.location.hash &&
        window.location.hash.includes('#/nonprod/lienholder/checkout/edit')) ||
      this.testCheckout
    ) {
      this.checkout = 'edit';
    } else {
      this.checkout = 'review';
    }
    this.loadLienholder();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('lienholder-edit', this.editBinded);
  }

  loadLienholder() {
    try {
      this.loading = true;
      LenderService.appId = SessionService.session
        ? SessionService.session.eAppId
        : this.mockSession.eAppId;
      LenderService.retrieveLienholder().then((res) => {
        if (res) {
          // pull from service
          const lender = LenderService.getLienholder();
          this.dispatchCompleteLoadingEvent();

          if (
            !lender ||
            Object.keys(lender).length === 0 ||
            !lender.currentLoanPayOffAmount
          ) {
            lender.currentLoanPayOffAmount = '';
            lender.lienHolderName = '';
            lender.lienHolderAccountNumber = '';
            stratum.publishTag(TagKey.LENDER_CHECKOUT_LENDER_MFE_LOAD, {
              replacements: {
                value: 'unsuccessful',
              },
            });
            this.dispatchFailedToLoadOrUpdateEvent();
          } else if (
            !lender.lienHolderName ||
            !lender.lienHolderAccountNumber
          ) {
            if (lender.lienHolderName) {
              stratum.publishTag(TagKey.LIENHOLDER_NAME_LENDER_NAME_PREFILLED);
            }
            if (lender.lienHolderAccountNumber) {
              stratum.publishTag(
                TagKey.LIENHOLDER_ACCOUNT_NUMBER_LENDER_ACCOUNT_NUMBER_PREFILLED
              );
            }
            stratum.publishTag(TagKey.LENDER_CHECKOUT_PAYOFF_AMOUNT_PREFILLED);

            this.dispatchUnsavedOrMissingFieldsEvent({
              detail: {
                actionName: 'missing',
              },
            });
            // default load edit component when missing fields
            if (
              (!environment.isProdEnv &&
                window.location.hash &&
                window.location.hash.includes(
                  '#/nonprod/lienholder/checkout/edit'
                )) ||
              this.testCheckout
            ) {
              this.checkout = 'edit';
            }
            stratum.publishTag(TagKey.LENDER_CHECKOUT_LENDER_MFE_LOAD, {
              replacements: {
                value: 'successful',
              },
            });
            this.loading = false;
          } else {
            stratum.publishTag(TagKey.LIENHOLDER_NAME_LENDER_NAME_PREFILLED);
            stratum.publishTag(
              TagKey.LIENHOLDER_ACCOUNT_NUMBER_LENDER_ACCOUNT_NUMBER_PREFILLED
            );
            stratum.publishTag(TagKey.LENDER_CHECKOUT_PAYOFF_AMOUNT_PREFILLED);
            stratum.publishTag(TagKey.LENDER_CHECKOUT_LENDER_MFE_LOAD, {
              replacements: {
                value: 'successful',
              },
            });
            this.loading = false;
          }
          this.lenderInfo = lender;
        } else {
          this.lenderInfo = {};
          this.debug.log('Failed to load lender info for review: ');
          stratum.publishTag(TagKey.LENDER_CHECKOUT_LENDER_MFE_LOAD, {
            replacements: {
              value: 'unsuccessful',
            },
          });
          this.dispatchFailedToLoadOrUpdateEvent();
        }
      });
    } catch (err) {
      this.lenderInfo = {};
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      this.debug.log('Failed to load lender info for review: ', err.message);
      stratum.publishTag(TagKey.LENDER_CHECKOUT_LENDER_MFE_LOAD, {
        replacements: {
          value: 'unsuccessful',
        },
      });
      // emit failed to load event to host app
      this.dispatchFailedToLoadOrUpdateEvent();
    }
  }

  render() {
    this.debug.log('rendered lienholder checkout');
    if (!this.isLoading && this.checkout === 'edit') {
      return html`<refi-lender-edit
        .lenderInfo="${this.lenderInfo}"
        @dispatch-close-event="${this.dispatchCloseEvent}"
        @dispatch-unsaved-missing-fields="${this
          .dispatchUnsavedOrMissingFieldsEvent}"
        @dispatch-failed-load-update-event="${this
          .dispatchFailedToLoadOrUpdateEvent}"
      ></refi-lender-edit>`;
    }
    if (!this.isLoading && this.checkout === 'review') {
      return html`<refi-lender-review
        .lenderInfo="${this.lenderInfo}"
      ></refi-lender-review>`;
    }
    return html``;
  }

  onEdit() {
    if (
      (!environment.isProdEnv &&
        window.location.hash &&
        window.location.hash.includes('#/nonprod/lienholder/checkout/edit')) ||
      this.testCheckout
    ) {
      this.checkout = 'edit';
    }
  }

  dispatchCompleteLoadingEvent() {
    this.dispatchEvent(
      new CustomEvent('checkout-completed-loading', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'lender-card',
        },
      })
    );
    this.debug.log('completed loading event fired');
  }

  dispatchFailedToLoadOrUpdateEvent() {
    this.dispatchEvent(
      new CustomEvent('failed-load-or-update', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'lender-card',
        },
      })
    );
    this.checkout = 'error';
    this.debug.log('failed to load or update event fired');
  }

  dispatchUnsavedOrMissingFieldsEvent(ev: any) {
    // action unsaved, missing
    this.dispatchEvent(
      new CustomEvent('unsaved-missing', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: ev.detail.actionName,
          id: 'lender-card',
        },
      })
    );
    this.debug.log(`unsaved or missing fields event: ${ev.detail.actionName}`);
  }

  dispatchCloseEvent(ev: any) {
    this.lenderInfo = LenderService.getLienholder();

    if (
      (!environment.isProdEnv &&
        window.location.hash &&
        window.location.hash.includes('#/nonprod/lienholder/checkout/edit')) ||
      this.testCheckout
    ) {
      this.checkout = 'review';
    }

    // Action is update, cancel
    this.dispatchEvent(
      new CustomEvent('close-editing', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: ev.detail.actionName,
          id: 'lender-card',
        },
      })
    );
    stratum.publishTag(TagKey.LENDER_CHECKOUT_LENDER_COMPONENT_COLLAPSED);
    this.debug.log(`cancel or update event: ${ev.detail.actionName}`);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'refi-lender-checkout': RefiLenderCheckout;
  }
}
